import * as THREE from 'three';
import { PHYSICS, ARENAS, computeStats, PARTS } from './PartData.js';

// ══════════════════════════════════════════════════════════════
//  BEYBLADE PHYSICS ENGINE v3
//  TRUE orbital movement via radial/tangential velocity decomposition
// ══════════════════════════════════════════════════════════════

export class BeybladePhysics {
  constructor(beyConfig, startPos, arenaType) {
    this.config = beyConfig;
    this.stats = computeStats(beyConfig.layer, beyConfig.core, beyConfig.tip);
    this.attackType = PARTS.layers[beyConfig.layer]?.attackType || 'smash';
    this.arena = ARENAS[arenaType];

    // Position & velocity
    this.position = new THREE.Vector3(startPos.x, 0.5, startPos.z);
    this.velocity = new THREE.Vector3(0, 0, 0);

    // Spin
    this.spinRPM = PHYSICS.LAUNCH_SPIN;
    this.spinDirection = startPos.x > 0 ? -1 : 1;
    this.angle = 0;

    // Orbital tracking
    this.orbitPhase = Math.atan2(startPos.z, startPos.x);
    this.orbitalSpeed = 0; // tracked for spin cost calc

    // Wobble / tilt
    this.wobble = 0;
    this.wobblePhase = Math.random() * Math.PI * 2;

    // State
    this.alive = true;
    this.burstOut = false;
    this.knockedOut = false;
    this.launched = false;

    // Collision tracking
    this.lastCollision = 0;
    this.lastCollisionForce = 0;
    this.lastWallHit = 0;
    this.collisionCount = 0;
    this.knockbackVel = new THREE.Vector3();
    this._collisionHandledFrame = -1;
    this.lastCritical = false;          // was last collision a critical hit?

    // Per-bey randomness seed — makes each instance slightly different
    this._rngSeed = Math.random();
    this._driftPhase = Math.random() * Math.PI * 2;
    this._spinVariance = 0.85 + Math.random() * 0.3; // 0.85–1.15 spin decay multiplier

    // -- Derived physics from stats --
    this.mass = 0.8 + this.stats.weight * 0.35;
    this.attackPower = 0.5 + this.stats.attack * 0.2;
    this.defensePower = 0.5 + this.stats.defense * 0.2;
    this.staminaMult = 0.4 + this.stats.stamina * 0.12;
    this.speedMult = 0.5 + this.stats.speed * 0.15;

    // Tip behaviour — affects orbit characteristics
    this.radialFriction = 0.93;     // friction on inward/outward motion
    this.tangentialFriction = 0.995; // friction on orbital motion (very low!)
    this.movementDrive = 0;
    this.gripFactor = 1.0;
    this.orbitStrength = 1.0;
    this.preferredRadius = 0.4; // fraction of arena radius where bey likes to orbit

    switch (beyConfig.tip) {
      case 'attack':
        this.radialFriction = 0.90;      // low = gravity pulls strongly inward
        this.tangentialFriction = 0.995;
        this.movementDrive = 2.0;
        this.gripFactor = 0.7;
        this.orbitStrength = 0.8;         // LOW — attack tips don't orbit, they roam
        this.preferredRadius = 0.22;     // wants to be near center for collisions
        break;
      case 'stamina':
        this.radialFriction = 0.91;
        this.tangentialFriction = 0.998;
        this.movementDrive = 0.15;
        this.gripFactor = 0.9;
        this.orbitStrength = 0.9;
        this.preferredRadius = 0.28;
        break;
      case 'defense':
        this.radialFriction = 0.89;
        this.tangentialFriction = 0.994;
        this.movementDrive = 0.2;
        this.gripFactor = 1.5;
        this.orbitStrength = 0.7;
        this.preferredRadius = 0.24;
        break;
      case 'balance':
        this.radialFriction = 0.91;
        this.tangentialFriction = 0.996;
        this.movementDrive = 0.6;
        this.gripFactor = 1.0;
        this.orbitStrength = 0.9;
        this.preferredRadius = 0.28;
        break;
      case 'jolt':
        this.radialFriction = 0.89;
        this.tangentialFriction = 0.993;
        this.movementDrive = 2.4;
        this.gripFactor = 0.6;
        this.orbitStrength = 0.7;
        this.preferredRadius = 0.20;     // wildly aggressive, near center
        break;
      case 'orbit':
        this.radialFriction = 0.90;
        this.tangentialFriction = 0.999;
        this.movementDrive = 0.1;
        this.gripFactor = 0.95;
        this.orbitStrength = 0.8;
        this.preferredRadius = 0.26;
        break;
      case 'destroy':
        this.radialFriction = 0.88;
        this.tangentialFriction = 0.992;
        this.movementDrive = 3.0;      // most aggressive movement
        this.gripFactor = 0.5;
        this.orbitStrength = 0.6;
        this.preferredRadius = 0.18;   // center-seeking berserker
        break;
      case 'bearing':
        this.radialFriction = 0.92;
        this.tangentialFriction = 0.9995; // almost no tangential friction
        this.movementDrive = 0.05;
        this.gripFactor = 1.0;
        this.orbitStrength = 1.0;
        this.preferredRadius = 0.30;
        break;
      case 'xtreme':
        this.radialFriction = 0.89;
        this.tangentialFriction = 0.994;
        this.movementDrive = 1.8;
        this.gripFactor = 0.65;
        this.orbitStrength = 0.75;
        this.preferredRadius = 0.20;
        break;
      case 'volcanic':
        this.radialFriction = 0.90;
        this.tangentialFriction = 0.996;
        this.movementDrive = 0.3;
        this.gripFactor = 1.3;
        this.orbitStrength = 0.85;
        this.preferredRadius = 0.25;
        break;
    }
  }

  launch(targetX, targetZ) {
    this.launched = true;
    const dx = targetX - this.position.x;
    const dz = targetZ - this.position.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 0.01) return;

    const speed = 5 + this.speedMult * 3;
    const towardX = dx / len;
    const towardZ = dz / len;
    // Strong tangential launch for immediate orbital motion
    const tangX = -towardZ * this.spinDirection;
    const tangZ = towardX * this.spinDirection;

    this.velocity.set(
      towardX * speed * 0.4 + tangX * speed * 0.8,
      0,
      towardZ * speed * 0.4 + tangZ * speed * 0.8
    );
  }

  update(dt, otherBey) {
    if (!this.alive || !this.launched) return;

    const R = this.arena.radius;
    const depth = this.arena.bowlDepth;
    const distFromCenter = Math.sqrt(this.position.x ** 2 + this.position.z ** 2);
    const spinRatio = this.spinRPM / PHYSICS.LAUNCH_SPIN; // 0-1

    // ══════════════════════════════════════
    //  SPIN DECAY (with per-bey variance)
    // ══════════════════════════════════════
    const baseDecay = (PHYSICS.SPIN_DECAY_BASE / this.staminaMult) * this._spinVariance;
    const knockbackSpeed = Math.sqrt(this.knockbackVel.x ** 2 + this.knockbackVel.z ** 2);
    const movementDecay = knockbackSpeed * 0.8;
    const wobbleDecay = this.wobble * 15;
    // Random micro-fluctuation in decay so identical builds don't tie
    const decayJitter = 1.0 + (Math.sin(performance.now() * 0.0017 + this._rngSeed * 100) * 0.08);
    this.spinRPM -= (baseDecay + movementDecay + wobbleDecay) * dt * 60 * decayJitter;

    // Wobble increases as spin drops
    if (this.spinRPM < 3500) {
      this.wobble = Math.min(0.5, ((3500 - this.spinRPM) / 3500) * 0.5);
    } else {
      this.wobble *= 0.98;
    }

    // ══════════════════════════════════════
    //  DECOMPOSE VELOCITY: radial vs tangential
    // ══════════════════════════════════════
    let radialSpeed = 0;
    let tangentialSpeed = 0;
    let radNx = 0, radNz = 0; // unit radial (outward from center)
    let tanNx = 0, tanNz = 1; // unit tangential

    if (distFromCenter > 0.05) {
      radNx = this.position.x / distFromCenter;
      radNz = this.position.z / distFromCenter;
      tanNx = -radNz * this.spinDirection;
      tanNz = radNx * this.spinDirection;

      // Project velocity onto radial and tangential
      radialSpeed = this.velocity.x * radNx + this.velocity.z * radNz;
      tangentialSpeed = this.velocity.x * tanNx + this.velocity.z * tanNz;
    }

    // ══════════════════════════════════════
    //  BOWL GRAVITY — pull toward center
    //  This is THE dominant force. Beys should always tend inward.
    //  As spin decays, gravity dominates gyroscopic stability.
    // ══════════════════════════════════════
    if (distFromCenter > 0.05) {
      const slopeStrength = (2 * depth * distFromCenter) / (R * R);
      // Gravity ramps up aggressively as spin drops
      const gravMultiplier = 1.0 + (1.0 - spinRatio) * 1.5;
      const gravForce = slopeStrength * PHYSICS.BOWL_GRAVITY * gravMultiplier;
      radialSpeed -= gravForce * dt;

      // CONSTANT CENTER PULL — always-on weak pull toward center
      // independent of bowl slope, ensures beys never orbit forever
      const centerPull = 1.5 + (1.0 - spinRatio) * 3.0;
      radialSpeed -= centerPull * dt;
    }

    // ══════════════════════════════════════
    //  GYROSCOPIC PRECESSION — ORBITAL FORCE
    //  A spinning top on a slope precesses perpendicular to gravity.
    //  This creates the characteristic circular orbiting motion.
    //  TAPERED: force reduces past the preferred orbit radius to
    //  prevent runaway acceleration near the walls.
    // ══════════════════════════════════════
    if (distFromCenter > 0.1 && this.spinRPM > 300) {
      const slopeHere = (2 * depth * distFromCenter) / (R * R);

      // Taper orbital force past the preferred radius
      // Full force up to preferredR, fades to 20% at the wall
      const preferredR = R * this.preferredRadius;
      let orbitTaper = 1.0;
      if (distFromCenter > preferredR) {
        const excess = (distFromCenter - preferredR) / (R - preferredR);
        orbitTaper = Math.max(0.2, 1.0 - excess * 0.8);
      }

      const orbitForce = PHYSICS.ORBIT_FORCE *
        spinRatio *
        slopeHere *
        this.orbitStrength *
        orbitTaper;

      tangentialSpeed += orbitForce * dt;
    }

    // ══════════════════════════════════════
    //  ORBIT RADIUS BIAS (very soft)
    //  Weak preference, never fights gravity. Only provides a
    //  gentle outward nudge when bey is very deep inside target.
    // ══════════════════════════════════════
    if (this.spinRPM > 500) {
      const dynamicPref = this.preferredRadius * (0.20 + spinRatio * 0.80);
      const targetR = R * dynamicPref;
      const radiusDiff = targetR - distFromCenter;
      // Only push outward very weakly if deep inside. Never resist inward travel.
      if (radiusDiff > 0) {
        radialSpeed += radiusDiff * 0.25 * spinRatio * dt;
      }
    }

    // ══════════════════════════════════════
    //  WOBBLE → RADIAL PERTURBATION
    //  At low RPM, wobble destabilizes the orbit, causing the bey
    //  to drift inward/outward erratically. This breaks stable orbits.
    // ══════════════════════════════════════
    if (this.wobble > 0.02) {
      const t = performance.now() * 0.003;
      const wobbleDrift = this.wobble * 5.0;     // stronger wobble disturbance
      radialSpeed += Math.sin(t * 2.1 + this.orbitPhase * 7) * wobbleDrift * dt;
      tangentialSpeed += Math.cos(t * 1.7 + this.orbitPhase * 3) * wobbleDrift * dt * 0.4;
    }

    // ══════════════════════════════════════
    //  RANDOM MICRO-DRIFT
    //  Constant small random perturbation prevents perfectly
    //  symmetric orbits and makes every battle unique.
    // ══════════════════════════════════════
    {
      const t = performance.now() * 0.001;
      const seed = this._driftPhase;
      const microStr = 1.2 * spinRatio;
      radialSpeed += Math.sin(t * 1.3 + seed * 50) * microStr * dt;
      radialSpeed += Math.sin(t * 3.7 + seed * 123) * microStr * 0.4 * dt;
      tangentialSpeed += Math.cos(t * 2.1 + seed * 77) * microStr * 0.3 * dt;
    }

    // ══════════════════════════════════════
    //  SELF-DRIVEN ERRATIC MOVEMENT (attack tips)
    // ══════════════════════════════════════
    if (this.movementDrive > 0 && this.spinRPM > 1500) {
      const t = performance.now() * 0.002;
      const driveStr = this.movementDrive * spinRatio * 2.0;
      // Erratic radial perturbation causes orbit to shift, creating collisions
      radialSpeed += Math.sin(t * 3.7 + this.orbitPhase * 5) * driveStr * dt;
      tangentialSpeed += Math.cos(t * 2.3 + this.orbitPhase * 3) * driveStr * dt * 0.5;
      // Occasional sudden lunge  (unpredictable aggression)
      if (Math.random() < 0.005) {
        radialSpeed += (Math.random() - 0.4) * driveStr * 6;
      }
    }

    // ══════════════════════════════════════
    //  CENTER AGGRESSION (attack/jolt tips)
    //  Instead of seeking the opponent (which can steer outward),
    //  attack tips get a strong constant pull toward center where
    //  collisions naturally happen. This creates the aggressive
    //  darting-through-center pattern real attack beys have.
    // ══════════════════════════════════════
    if (this.movementDrive > 0.5 && distFromCenter > 0.5) {
      // Pulsing center-dive: attack tips periodically lunge inward
      const pulseCycle = Math.sin(performance.now() * 0.002 + this._rngSeed * 60);
      const centerDive = this.movementDrive * 2.5 * spinRatio * Math.max(0, pulseCycle);
      radialSpeed -= centerDive * dt;
    }

    // ══════════════════════════════════════
    //  DIRECTIONAL FRICTION
    //  KEY INSIGHT: Friction acts differently on radial vs tangential motion.
    //  Radial (in/out) is damped heavily — bey shouldn't oscillate wildly.
    //  Tangential (orbital) is damped very lightly — preserves orbit speed.
    // ══════════════════════════════════════
    let rFric = this.radialFriction;
    let tFric = this.tangentialFriction;
    if (this.arena.hasIce) {
      rFric = rFric + (1 - rFric) * 0.6;
      tFric = tFric + (1 - tFric) * 0.5;
    }
    if (this.arena.hasDesert) {
      // Sandy terrain — higher friction, slower orbits
      rFric *= 0.95;
      tFric *= 0.97;
    }
    if (this.arena.hasThunder) {
      // Charged surface — occasional speed boosts
      if (Math.random() < 0.01) {
        tangentialSpeed *= 1.15;
      }
    }
    // Soften radial friction as spin drops — lets gravity pull bey inward more
    rFric = rFric + (1.0 - rFric) * (1.0 - spinRatio) * 0.45;
    radialSpeed *= rFric;
    tangentialSpeed *= tFric;

    // ── Max tangential speed cap ──
    // Prevents runaway orbital speed especially near walls.
    // Scales with spin — slower beys orbit slower.
    const maxTangential = (4 + spinRatio * 5) * this.orbitStrength;
    if (Math.abs(tangentialSpeed) > maxTangential) {
      tangentialSpeed = Math.sign(tangentialSpeed) * maxTangential;
    }

    // ── Extra friction near walls ──
    // When bey drifts past 75% of arena radius, ramp up tangential friction
    // to prevent high-speed wall-scraping.
    const wallZone = R * 0.75;
    if (distFromCenter > wallZone) {
      const wallProx = (distFromCenter - wallZone) / (R - wallZone); // 0→1
      const wallBrake = 1.0 - wallProx * 0.06; // up to 6% extra friction per frame
      tangentialSpeed *= wallBrake;
    }

    // Track orbital speed for HUD / effects
    this.orbitalSpeed = Math.abs(tangentialSpeed);

    // ══════════════════════════════════════
    //  RECOMPOSE VELOCITY from radial + tangential
    // ══════════════════════════════════════
    if (distFromCenter > 0.05) {
      this.velocity.x = radNx * radialSpeed + tanNx * tangentialSpeed;
      this.velocity.z = radNz * radialSpeed + tanNz * tangentialSpeed;
    } else {
      // Very near center — apply uniform light friction
      this.velocity.x *= 0.98;
      this.velocity.z *= 0.98;
    }

    // Knockback decays
    this.knockbackVel.multiplyScalar(0.88);

    // ══════════════════════════════════════
    //  BEY vs BEY COLLISION (processed once only)
    // ══════════════════════════════════════
    const frameId = Math.round(performance.now()); // frame identifier
    if (otherBey && otherBey.alive && otherBey.launched &&
        this._collisionHandledFrame !== frameId &&
        otherBey._collisionHandledFrame !== frameId) {
      const dx = otherBey.position.x - this.position.x;
      const dz = otherBey.position.z - this.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const collisionDist = 1.4;

      if (dist < collisionDist && dist > 0.001) {
        const nx = dx / dist;
        const nz = dz / dist;

        const relVx = (this.velocity.x + this.knockbackVel.x) -
                       (otherBey.velocity.x + otherBey.knockbackVel.x);
        const relVz = (this.velocity.z + this.knockbackVel.z) -
                       (otherBey.velocity.z + otherBey.knockbackVel.z);
        const approachSpeed = relVx * nx + relVz * nz;

        if (approachSpeed > 0) {
          // Mark both as handled so bey2.update won't double-process
          this._collisionHandledFrame = frameId;
          otherBey._collisionHandledFrame = frameId;

          this.collisionCount++;
          otherBey.collisionCount++;

          const totalMass = this.mass + otherBey.mass;
          const thisSpinEnergy = this.spinRPM / PHYSICS.LAUNCH_SPIN;
          const otherSpinEnergy = otherBey.spinRPM / PHYSICS.LAUNCH_SPIN;

          // ── CRITICAL HIT ROLL ──
          // Higher attack increases crit chance, higher defense reduces it
          const critChance = PHYSICS.CRITICAL_HIT_CHANCE;
          const thisCrit  = Math.random() < (critChance * (0.7 + this.attackPower * 0.15));
          const otherCrit = Math.random() < (critChance * (0.7 + otherBey.attackPower * 0.15));
          const thisCritMult  = thisCrit  ? PHYSICS.CRITICAL_HIT_MULT : 1.0;
          const otherCritMult = otherCrit ? PHYSICS.CRITICAL_HIT_MULT : 1.0;
          this.lastCritical = thisCrit;
          otherBey.lastCritical = otherCrit;

          // Random impact variance (±20%) — no two hits feel the same
          const hitVariance1 = 0.8 + Math.random() * 0.4;
          const hitVariance2 = 0.8 + Math.random() * 0.4;

          // ══ ATTACK TYPE MODIFIERS ══
          // Each attack type creates fundamentally different collision behavior
          const thisAT = this.attackType;
          const otherAT = otherBey.attackType;

          // Knockback multiplier by attack type
          const knockMod = (at) => at === 'smash' ? 1.6 : at === 'barrage' ? 0.65 : 0.85;
          // Spin damage multiplier by attack type
          const dmgMod   = (at) => at === 'smash' ? 1.5 : at === 'barrage' ? 0.70 : 0.75;

          // ── KNOCKBACK ──
          const knockbackBase = PHYSICS.KNOCKBACK_FORCE;
          const impactForce = knockbackBase * (1 + approachSpeed * 0.5);

          const thisKnockPower = impactForce *
            (this.attackPower / otherBey.defensePower) *
            (this.mass / totalMass) *
            (0.3 + thisSpinEnergy * 0.7) *
            (1 / otherBey.gripFactor) *
            thisCritMult * hitVariance1 * knockMod(thisAT);

          const otherKnockPower = impactForce *
            (otherBey.attackPower / this.defensePower) *
            (otherBey.mass / totalMass) *
            (0.3 + otherSpinEnergy * 0.7) *
            (1 / this.gripFactor) *
            otherCritMult * hitVariance2 * knockMod(otherAT);

          this.knockbackVel.x -= nx * otherKnockPower;
          this.knockbackVel.z -= nz * otherKnockPower;
          otherBey.knockbackVel.x += nx * thisKnockPower;
          otherBey.knockbackVel.z += nz * thisKnockPower;

          // Elastic velocity exchange
          const elasticity = 0.75;
          const j = (1 + elasticity) * approachSpeed / totalMass;
          this.velocity.x -= j * otherBey.mass * nx;
          this.velocity.z -= j * otherBey.mass * nz;
          otherBey.velocity.x += j * this.mass * nx;
          otherBey.velocity.z += j * this.mass * nz;

          // ── SPIN DAMAGE (with crit multiplier + variance) ──
          const spinDmgBase = PHYSICS.COLLISION_SPIN_LOSS;

          const spinDamageToOther = spinDmgBase *
            (this.attackPower / otherBey.defensePower) *
            (0.5 + thisSpinEnergy * 0.5) *
            (1 + approachSpeed * 0.3) *
            (1 + (this.collisionCount > 5 ? 0.3 : 0)) *
            thisCritMult * hitVariance1 * dmgMod(thisAT);

          const spinDamageToSelf = spinDmgBase *
            (otherBey.attackPower / this.defensePower) *
            (0.5 + otherSpinEnergy * 0.5) *
            (1 + approachSpeed * 0.3) *
            (1 + (otherBey.collisionCount > 5 ? 0.3 : 0)) *
            otherCritMult * hitVariance2 * dmgMod(otherAT);

          let spinSteal = 0;
          if (this.spinDirection !== otherBey.spinDirection) {
            spinSteal = Math.min(spinDamageToOther * 0.25, otherBey.spinRPM * 0.05);
          }

          // ══ BARRAGE: extra spin steal on every hit ══
          // Barrage layers grind spin away continuously like sandpaper
          if (thisAT === 'barrage') {
            const barrageSteal = spinDamageToOther * 0.20;
            spinSteal += barrageSteal;
          }
          if (otherAT === 'barrage') {
            const barrageSteal = spinDamageToSelf * 0.20;
            spinSteal -= barrageSteal; // opponent steals from us
          }

          this.spinRPM -= spinDamageToSelf;
          this.spinRPM += spinSteal;
          otherBey.spinRPM -= spinDamageToOther;
          otherBey.spinRPM -= spinSteal;

          // ══ UPPER ATTACK: destabilize opponent ══
          // Upper blades angle upward — on contact they scoop under
          // the opponent, increasing wobble (tilt instability).
          // This doesn't do raw damage but disrupts spin axis.
          if (thisAT === 'upper') {
            const upperForce = 0.06 + thisSpinEnergy * 0.10;
            otherBey.wobble = Math.min(0.50, otherBey.wobble + upperForce * thisCritMult);
            // Vertical displacement — push opponent outward & up
            otherBey.knockbackVel.x += nx * 1.5 * thisSpinEnergy;
            otherBey.knockbackVel.z += nz * 1.5 * thisSpinEnergy;
          }
          if (otherAT === 'upper') {
            const upperForce = 0.06 + otherSpinEnergy * 0.10;
            this.wobble = Math.min(0.50, this.wobble + upperForce * otherCritMult);
            this.knockbackVel.x -= nx * 1.5 * otherSpinEnergy;
            this.knockbackVel.z -= nz * 1.5 * otherSpinEnergy;
          }

          // ══ SMASH vs SMASH: both take increased recoil ══
          if (thisAT === 'smash' && otherAT === 'smash') {
            this.spinRPM -= spinDmgBase * 0.15;
            otherBey.spinRPM -= spinDmgBase * 0.15;
          }

          this.spinRPM = Math.max(0, this.spinRPM);
          otherBey.spinRPM = Math.max(0, otherBey.spinRPM);

          // ── SEPARATE ──
          const overlap = collisionDist - dist;
          const sepStr = overlap * 0.6;
          this.position.x -= nx * sepStr;
          this.position.z -= nz * sepStr;
          otherBey.position.x += nx * sepStr;
          otherBey.position.z += nz * sepStr;

          // ── BURST CHECK ──
          if (this.spinRPM < PHYSICS.BURST_THRESHOLD) {
            const burstChance = 0.03 +
              (PHYSICS.BURST_THRESHOLD - this.spinRPM) / PHYSICS.BURST_THRESHOLD * 0.15 +
              approachSpeed * 0.03 +
              (thisKnockPower > 10 ? 0.05 : 0);
            if (Math.random() < burstChance) {
              this.burstOut = true;
              this.alive = false;
            }
          }
          if (otherBey.spinRPM < PHYSICS.BURST_THRESHOLD) {
            const burstChance = 0.03 +
              (PHYSICS.BURST_THRESHOLD - otherBey.spinRPM) / PHYSICS.BURST_THRESHOLD * 0.15 +
              approachSpeed * 0.03 +
              (otherKnockPower > 10 ? 0.05 : 0);
            if (Math.random() < burstChance) {
              otherBey.burstOut = true;
              otherBey.alive = false;
            }
          }

          // Mark collision for effects
          const now = performance.now();
          this.lastCollision = now;
          otherBey.lastCollision = now;
          this.lastCollisionForce = otherKnockPower;
          otherBey.lastCollisionForce = thisKnockPower;
        }
      }
    }

    // ══════════════════════════════════════
    //  WALL COLLISION
    // ══════════════════════════════════════
    if (distFromCenter > R - 0.6) {
      if (this.arena.hasWalls) {
        const wnx = -this.position.x / distFromCenter;
        const wnz = -this.position.z / distFromCenter;
        const totalVx = this.velocity.x + this.knockbackVel.x;
        const totalVz = this.velocity.z + this.knockbackVel.z;
        const dot = totalVx * wnx + totalVz * wnz;

        if (dot < 0) {
          const bounce = this.arena.wallBounce;
          this.velocity.x -= (1 + bounce) * dot * wnx * 0.7;
          this.velocity.z -= (1 + bounce) * dot * wnz * 0.7;
          this.knockbackVel.x *= 0.3;
          this.knockbackVel.z *= 0.3;

          const wallImpactForce = Math.abs(dot);
          this.spinRPM -= PHYSICS.WALL_SPIN_LOSS * (1 + wallImpactForce * 0.5);

          const pushR = R - 0.65;
          this.position.x = (this.position.x / distFromCenter) * pushR;
          this.position.z = (this.position.z / distFromCenter) * pushR;

          this.lastWallHit = performance.now();
        }
      } else {
        if (distFromCenter > R + 0.3) {
          this.knockedOut = true;
          this.alive = false;
        }
      }
    }

    // ══════════════════════════════════════
    //  LAVA DAMAGE
    // ══════════════════════════════════════
    if (this.arena.hasLava && distFromCenter < 1.2) {
      const lavaDamage = (1.2 - distFromCenter) * 100;
      this.spinRPM -= lavaDamage * dt;
    }

    // ══════════════════════════════════════
    //  APPLY VELOCITY TO POSITION
    // ══════════════════════════════════════
    this.position.x += (this.velocity.x + this.knockbackVel.x) * dt;
    this.position.z += (this.velocity.z + this.knockbackVel.z) * dt;

    // ══════════════════════════════════════
    //  Y POSITION — follow bowl surface
    // ══════════════════════════════════════
    const rClamp = Math.min(distFromCenter, R);
    const rNorm = rClamp / R;
    this.position.y = depth * (rNorm * rNorm - 1) + 0.35;

    // ══════════════════════════════════════
    //  SPIN ANGLE (visual rotation)
    // ══════════════════════════════════════
    this.angle += this.spinRPM * (Math.PI * 2 / 60) * dt * this.spinDirection;

    // ══════════════════════════════════════
    //  DEATH CHECKS
    // ══════════════════════════════════════
    this.spinRPM = Math.max(0, this.spinRPM);
    if (this.spinRPM <= 0) {
      this.alive = false;
    }
  }

  applyToMesh(group) {
    if (!group) return;
    group.position.copy(this.position);
    group.rotation.y = this.angle;

    if (this.wobble > 0.01) {
      this.wobblePhase += 0.15;
      group.rotation.x = Math.sin(this.wobblePhase) * this.wobble;
      group.rotation.z = Math.cos(this.wobblePhase * 1.3) * this.wobble;
    } else {
      group.rotation.x = 0;
      group.rotation.z = 0;
    }

    // Burst scatter
    if (this.burstOut && group.userData.parts) {
      const parts = group.userData.parts;
      const scatter = (obj, dirY, spread) => {
        if (!obj) return;
        obj.position.x += (Math.random() - 0.5) * spread;
        obj.position.y += dirY;
        obj.position.z += (Math.random() - 0.5) * spread;
        obj.rotation.x += (Math.random() - 0.5) * 0.2;
        obj.rotation.z += (Math.random() - 0.5) * 0.2;
      };
      scatter(parts.layerGroup, 0.2, 0.4);
      scatter(parts.discGroup, 0.1, 0.3);
      scatter(parts.bolt, 0.3, 0.2);
      scatter(parts.tipGroup, 0.05, 0.15);
    }
  }
}
